import { motion } from 'framer-motion';
import { ScrollReveal, StaggeredList, StaggeredListItem } from './ScrollReveal';
import { SubtleStars } from './AtmosphericGrain';

export function AboutSection() {
  return (
    <section id="about" className="relative py-20 overflow-hidden">
      {/* Subtle atmospheric elements */}
      <SubtleStars count={25} className="opacity-30" />

      {/* Subtle blue ambient light */}
      <div
        className="absolute top-1/3 right-0 w-[600px] h-[600px] pointer-events-none opacity-5 blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(100,150,180,0.2) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Label */}
        <ScrollReveal className="mb-editorial-md">
          <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider">
            <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
            <span>Background</span>
          </div>
        </ScrollReveal>

        {/* Strong opening statement - Large and prominent */}
        <div className="mb-editorial-lg">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1 }}
            className="text-editorial-question font-bold max-w-6xl text-white"
            style={{
              textShadow: '0 0 60px rgba(255,255,255,0.2), 0 2px 40px rgba(0,0,0,0.6)',
            }}
          >
            I design technology that adapts to how we naturally work and learn.
          </motion.h2>
        </div>

        {/* Biography - asymmetric paragraphs for editorial feel */}
        <div className="mb-editorial-xl">
          <div className="grid grid-cols-12 gap-x-12 gap-y-editorial-md">
            {/* First paragraph - offset left */}
            <StaggeredList className="col-span-12 lg:col-span-8 lg:col-start-1">
              <StaggeredListItem>
                <p className="text-editorial-body text-slate-200/70 leading-relaxed">
                  I earned my bachelor's in Computer Science and Digital Arts from Dartmouth College ('26) and am passionate about creating technology that's intuitive, accessible, and human-centered. My work combines user research, interaction design, and technical development to build experiences that genuinely help people.
                </p>
              </StaggeredListItem>
            </StaggeredList>

            {/* Second paragraph - offset right */}
            <StaggeredList className="col-span-12 lg:col-span-9 lg:col-start-4">
              <StaggeredListItem>
                <p className="text-editorial-body text-slate-200/70 leading-relaxed">
                  I'm drawn to projects addressing accessibility needs and bridging cultural gaps through design—from UX research on wearable technology to developing tools for blind and low-vision users to creating language-learning games.
                </p>
              </StaggeredListItem>
            </StaggeredList>

            {/* Language note - small, right */}
            <StaggeredList className="col-span-12 lg:col-span-5 lg:col-start-8">
              <StaggeredListItem>
                <p className="text-editorial-meta text-slate-200/60 uppercase tracking-wider">
                  Languages: English • Spanish • Korean
                </p>
              </StaggeredListItem>
            </StaggeredList>
          </div>

          <ScrollReveal delay={0.6} className="mt-editorial-md">
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-cyan-500 text-slate-900 rounded-lg hover:bg-cyan-400 transition-all duration-300 font-medium hover:scale-105 shadow-lg shadow-cyan-500/20"
            >
              Get In Touch
            </a>
          </ScrollReveal>
        </div>

        {/* Capabilities - editorial typography organization without cards */}
        <div className="border-t border-cyan-900/10 pt-editorial-md">
          <div className="grid grid-cols-12 gap-x-12 gap-y-editorial-md">
            {/* Research */}
            <div className="col-span-12 lg:col-span-4">
              <ScrollReveal>
                <h3 className="text-editorial-statement text-cyan-300 mb-6 font-semibold">
                  Research
                </h3>
                <div className="space-y-3">
                  <div className="text-editorial-body text-slate-200/70">User Interviews</div>
                  <div className="text-editorial-body text-slate-200/70">Usability Testing</div>
                  <div className="text-editorial-body text-slate-200/70">Mixed Methods</div>
                  <div className="text-editorial-body text-slate-200/70">Survey Design</div>
                  <div className="text-editorial-body text-slate-200/70">Thematic Analysis</div>
                </div>
              </ScrollReveal>
            </div>

            {/* Design */}
            <div className="col-span-12 lg:col-span-4">
              <ScrollReveal delay={0.1}>
                <h3 className="text-editorial-statement text-cyan-300 mb-6 font-semibold">
                  Design
                </h3>
                <div className="space-y-3">
                  <div className="text-editorial-body text-slate-200/70">Product Design</div>
                  <div className="text-editorial-body text-slate-200/70">Interaction Design</div>
                  <div className="text-editorial-body text-slate-200/70">Prototyping</div>
                  <div className="text-editorial-body text-slate-200/70">Design Systems</div>
                  <div className="text-editorial-body text-slate-200/70">UI/UX Design</div>
                </div>
              </ScrollReveal>
            </div>

            {/* Build */}
            <div className="col-span-12 lg:col-span-4">
              <ScrollReveal delay={0.2}>
                <h3 className="text-editorial-statement text-cyan-300 mb-6 font-semibold">
                  Build
                </h3>
                <div className="space-y-3">
                  <div className="text-editorial-body text-slate-200/70">Python</div>
                  <div className="text-editorial-body text-slate-200/70">React / TypeScript</div>
                  <div className="text-editorial-body text-slate-200/70">Unity / C#</div>
                  <div className="text-editorial-body text-slate-200/70">HTML / CSS</div>
                  <div className="text-editorial-body text-slate-200/70">FastAPI</div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
